import { Expirable } from './support/Expirable';
import { State } from './support/State';

export interface IAppRelease {
    version: string; // ex: 1.0.0
    expires?: string; // ex: 2023-01-01
}

interface IActiveVersion {
    airspaceMVTUrlV2: string;

    // V4 was a breaking change b/c we started cropping features, so in tiles-client we need to merge instead of dedup
    airspaceAuthorityUrlV4: string;

    faaIsDown?: boolean;
    tilesAreStale?: boolean;
    appReleases?: IAppRelease[];
}

export class TileUrlProviderConfig {
    TILE_VERSION_URL: string;
    constructor() {
        throw new Error('TileUrlProviderConfig should be set through ioc');
    }
}

export class TileUrlProviderLogger {
    info = (msg: string) => {
        console.log(msg);
    }
}

const MAX_AGE_MS = 5 * 60 * 1000; // 5 mins

export class TileUrlProvider extends State {
    static inject = () => [TileUrlProviderConfig, TileUrlProviderLogger];

    constructor(private config: TileUrlProviderConfig, private log: TileUrlProviderLogger) {
        super({});
        this.activeVersionV2 = new Expirable<IActiveVersion>(
            () => fetch(this.config.TILE_VERSION_URL).then(res => res.json()),

            // * .8 otherwise it will always take 2 intervals
            // eg assume both interval and expire is 5m...
            //      interval zero ticks at t+0
            //      response ticks at t+1m
            //      inteval one ticks at t+5m (but response is only 4m old, so it re-uses it)
            //      interval two tickst at t+10m and fetches a new one, the old one was 9m old
            MAX_AGE_MS * .8
        );
    }

    private activeVersionV2: Expirable<IActiveVersion>;

    pollForActiveVersionChanges = () => {
        let previousResult: IActiveVersion;

        this.log.info(`TileUrlProvider polling every ${MAX_AGE_MS} ms`);

        setInterval(async () => {
            this.log.info(`TileUrlProvider polling ticked`);

            const result = await this.activeVersionV2.get();
            this.log.info(`TileUrlProvider polling got ${JSON.stringify(result)}`);

            if (!previousResult || JSON.stringify(result) !== JSON.stringify(previousResult)){
                this.log.info(`TileUrlProvider detected change, emitting`);
                this.setState({ });
            }

            previousResult = result;
        }, MAX_AGE_MS);
    }

    getAirspaceTileUrl = async (): Promise<string> => {
        const activeVersion = await this.activeVersionV2.get();
        return activeVersion.airspaceMVTUrlV2;
    }

    getAirspaceAuthorityTile = async ({ z, x, y }: { z: number, x: number, y: number }): Promise<string> => {
        const activeVersion = await this.activeVersionV2.get();

        return activeVersion.airspaceAuthorityUrlV4
            .replace('{z}', String(z))
            .replace('{x}', String(x))
            .replace('{y}', String(y));
    }

    getAuthorityErrataUrls = async (): Promise<string[]> => {
        const { airspaceAuthorityUrlV4 } = await this.activeVersionV2.get();

        // this is effectively coupled to AutoPylotAuthorizationErrataGenerator.supportedTileSets
        // note that adding a new errata should happen in this order
        // 1. do a full run that includes the new errata, at least as a supportedTileSets
        // 2. then add to here (so this doesn't fail on 404)
        // NOTE: we fail on 404 (architecture is for empty files over 404s) b/c we can't control the caching
        // of 404s in S3 (easily), much easier to create the empty files
        return [
            airspaceAuthorityUrlV4
                .replace('{z}', '0')
                .replace('{x}', '0')
                .replace('{y}', '0')
                .replace('geo.json', 'uasfm+airspace.errata-v2.json'),
            airspaceAuthorityUrlV4
                .replace('{z}', '0')
                .replace('{x}', '0')
                .replace('{y}', '0')
                .replace('geo.json', 'seams.errata-v2.json'),
            airspaceAuthorityUrlV4
                .replace('{z}', '0')
                .replace('{x}', '0')
                .replace('{y}', '0')
                .replace('geo.json', 'temporary-flight-restrictions.errata-v2.json'),
            airspaceAuthorityUrlV4
                .replace('{z}', '0')
                .replace('{x}', '0')
                .replace('{y}', '0')
                .replace('geo.json', 'parttime-nsufr.errata-v2.json'),
            airspaceAuthorityUrlV4
                .replace('{z}', '0')
                .replace('{x}', '0')
                .replace('{y}', '0')
                .replace('geo.json', 'nsufr.errata-v2.json')
        ];
    }

    getFAAStatus = async (): Promise<{ faaIsDown: boolean, tilesAreStale: boolean, appReleases: IAppRelease[]}> => {
        const activeVersion = await this.activeVersionV2.get();
        const faaIsDown = activeVersion.faaIsDown || false;
        const tilesAreStale = activeVersion.tilesAreStale || false;
        const appReleases = activeVersion.appReleases || [];
        return { faaIsDown, tilesAreStale, appReleases };
    }
}
